import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePatchState } from '@/shared/hooks';
import { capturePublicApi } from '../api/captureApi';
import type { InviteResolution } from '../api/captureApi';
import { useFunnelTracker } from '../hooks/useFunnelTracker';
import { PracticeHeader, StarPicker } from '../components/CapturePagePrimitives';
import { CaptureHappyPathFlow } from '../components/CaptureHappyPathFlow';
import { CaptureLowRatingFlow } from '../components/CaptureLowRatingFlow';
import { CaptureShareScreen } from '../components/CaptureShareScreen';
import styles from './ReviewCapturePage.module.css';

type Step = 'loading' | 'error' | 'rating' | 'happy' | 'low' | 'share';

interface PageState {
  step: Step;
  resolution: InviteResolution | null;
  rating: number | null;
  /** The review text on the Share screen when the patient arrives from a text hand-off. */
  draft: string;
  errorMsg: string;
}

export function ReviewCapturePage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const [state, set] = usePatchState<PageState>({
    step: 'loading',
    resolution: null,
    rating: null,
    draft: '',
    errorMsg: '',
  });
  const trackStep = useFunnelTracker(shortCode);

  // Force light + sunset on this public patient-facing page, restore on unmount
  useEffect(() => {
    const root = document.documentElement;
    const prevTheme = root.getAttribute('data-theme');
    const prevPalette = root.getAttribute('data-palette');
    root.setAttribute('data-theme', 'light');
    root.setAttribute('data-palette', 'sunset');
    return () => {
      if (prevTheme) root.setAttribute('data-theme', prevTheme);
      else root.removeAttribute('data-theme');
      if (prevPalette) root.setAttribute('data-palette', prevPalette);
      else root.removeAttribute('data-palette');
    };
  }, []);

  // Resolve invite + track open on mount
  useEffect(() => {
    if (!shortCode) return;
    capturePublicApi
      .resolve(shortCode)
      .then((res) => {
        const resolution = res.data ?? null;
        // A text hand-off: the patient already rated, answered and approved their draft in the
        // thread (only 5 stars get there). Open on the Share screen with that draft, never the stars.
        if (resolution?.handoffReady) {
          set({
            step: 'share',
            resolution,
            rating: 5,
            draft: resolution.approvedDraft ?? '',
          });
        } else {
          set({ step: 'rating', resolution });
        }
        capturePublicApi
          .trackOpen(shortCode)
          .catch((err: unknown) => console.debug('trackOpen failed', err));
      })
      .catch(() => set({ step: 'error', errorMsg: 'This review link is invalid or has expired.' }));
  }, [shortCode, set]);

  // Star rating is the only branch point: ≤4 stars → low-rating wizard, 5 stars → happy-path wizard
  function handleRatingChange(n: number) {
    trackStep('rating', n);
    set({ rating: n, step: n <= 4 ? 'low' : 'happy' });
  }

  const { step, resolution, rating } = state;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {step === 'loading' && (
          <div className={styles.center}>
            <div className={styles.spinner} aria-label="Loading" />
          </div>
        )}

        {step === 'error' && (
          <div className={styles.center}>
            <p className={styles.errorMsg}>{state.errorMsg}</p>
          </div>
        )}

        {step === 'rating' && resolution && (
          <>
            <PracticeHeader resolution={resolution} />
            <div className={styles.card}>
              {resolution.isTest && (
                <p className={styles.testBadge}>Test session — not counted in your stats</p>
              )}
              <div className={styles.hero}>Share your experience</div>
              <p className={styles.sub}>Tell us how it went - it'll only take a moment.</p>
              <StarPicker value={rating} onChange={handleRatingChange} />
            </div>
          </>
        )}

        {step === 'happy' && resolution && (
          <CaptureHappyPathFlow
            resolution={resolution}
            shortCode={shortCode!}
            rating={rating}
            onBackToIntro={() => set({ step: 'rating' })}
          />
        )}

        {step === 'share' && resolution && (
          <>
            <PracticeHeader resolution={resolution} />
            {resolution.isTest && (
              <p className={styles.testBadge}>Test session — not counted in your stats</p>
            )}
            <CaptureShareScreen
              resolution={resolution}
              shortCode={shortCode!}
              rating={rating}
              draft={state.draft}
              draftLoading={false}
              onChangeDraft={(draft) => set({ draft })}
              copyToClipboard
              onBack={() => set({ step: 'rating', rating: null })}
            />
          </>
        )}

        {step === 'low' && resolution && (
          <CaptureLowRatingFlow
            resolution={resolution}
            shortCode={shortCode!}
            rating={rating}
            onBackToIntro={() => set({ step: 'rating' })}
          />
        )}
      </div>
    </div>
  );
}
